import { Box, Button, ConditionWrapper, Dropdown, Flex, Spacing, Table, Text } from '@approvalmax/ui/src/components';
import { BodyCellProps } from '@approvalmax/ui/src/components/Table/components';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { FC, memo, useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';

import { showSetDelegatePopup } from '../../../../actions';
import { getManageUsersCommand } from '../../../../selectors/commandSelectors';
import type { UserRow } from '../../UserList.types';
import { messages } from './DelegateCell.messages';

const DelegateCell: FC<BodyCellProps<UserRow>> = memo((props) => {
    const { item, hovered, value } = props;

    const [tooltipOpen, tooltipOpenToogle] = useToggle(false);
    const dispatch = useDispatch();
    const company = useSelector(selectors.navigation.getActiveCompany);
    const companyTeam = useSelector((state) => selectors.company.getCompanyTeam(state, company));
    const allUsers = useSelector(selectors.user.getUsers);
    const manageUsersCommand = useSelector(getManageUsersCommand);

    const [delegateDisplayName, delegateRemovedFromCompany] = useMemo(() => {
        let delegateDisplayName = '';
        let delegateRemovedFromCompany = false;

        if (value) {
            const delegateUser = companyTeam.find((user) => user.id === value);

            if (delegateUser) {
                delegateDisplayName = delegateUser.displayName;
            } else {
                delegateDisplayName = allUsers.find((x) => x.id === value)!.displayName;
                delegateRemovedFromCompany = true;
            }
        }

        return [delegateDisplayName, delegateRemovedFromCompany];
    }, [allUsers, companyTeam, value]);

    const showHint = !delegateDisplayName;
    const isDelegateButtonDisabled =
        manageUsersCommand.disabled ||
        manageUsersCommand.hidden ||
        company.flags.isRetired ||
        company.isReadonly ||
        (company.flags.isWorkflowManager && item.role !== domain.CompanyUserRole.Participant);

    const onDelegateButtonClick = useCallback(() => {
        if (manageUsersCommand.hidden || manageUsersCommand.disabled) {
            return;
        }

        dispatch(showSetDelegatePopup(item.id));
    }, [dispatch, item.id, manageUsersCommand.disabled, manageUsersCommand.hidden]);

    const onTooltipOpen = useCallback(() => {
        tooltipOpenToogle();
    }, [tooltipOpenToogle]);

    return (
        <Table.Cell {...props}>
            <ConditionWrapper
                condition={showHint}
                wrapperIf={(children) => (
                    <Dropdown
                        open={tooltipOpen}
                        onOpen={onTooltipOpen}
                        closeOnClickInnerButton
                        activator={children}
                        isClicking
                        width='max-content'
                    >
                        <Box spacing='8'>
                            <Flex direction='column' spacing='4'>
                                <Text fontWeight='bold' color='midnight100' font='body' fontSize='xsmall'>
                                    {messages.title}
                                </Text>

                                <Text wrap font='body' fontSize='xsmall'>
                                    {messages.description({ br: <br /> })}
                                </Text>

                                <Spacing height={12} />

                                <Flex justifyContent='end'>
                                    <Button
                                        size='xsmall'
                                        color='blue10'
                                        onClick={onDelegateButtonClick}
                                        disabled={isDelegateButtonDisabled}
                                        title={
                                            delegateRemovedFromCompany ? messages.delegateRemovedFromCompany : undefined
                                        }
                                    >
                                        <Text fontSize='small' font='body'>
                                            {messages.selectDelegateText}
                                        </Text>
                                    </Button>
                                </Flex>
                            </Flex>
                        </Box>
                    </Dropdown>
                )}
            >
                <Button
                    size='small'
                    noPadding
                    noUppercase
                    onClick={showHint ? undefined : onDelegateButtonClick}
                    disabled={isDelegateButtonDisabled}
                    title={delegateRemovedFromCompany ? messages.delegateRemovedFromCompany : undefined}
                    variant='text'
                    color='blue100'
                >
                    {value ? (
                        <Text font='body' color='black100' fontWeight='regular' fontSize='small'>
                            {delegateDisplayName}
                        </Text>
                    ) : (
                        !isDelegateButtonDisabled &&
                        (hovered || tooltipOpen) && (
                            <Text font='body' fontWeight='regular' fontSize='small'>
                                {messages.selectDelegateText}
                            </Text>
                        )
                    )}
                </Button>
            </ConditionWrapper>
        </Table.Cell>
    );
});

DelegateCell.displayName = 'DelegateCell';

export default DelegateCell;
